import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";

export default function AdminLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email.trim(), password);
            toast.success("Signed in");
            navigate("/admin", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string" ? detail : "Invalid credentials"
            );
        } finally {
            setBusy(false);
        }
    };

    const inputCls =
        "w-full bg-white border border-[#0a0a0a]/15 px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#0a0a0a] placeholder:text-[#a1a1aa]";

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-screen bg-[#fdfdfd] grid lg:grid-cols-2"
        >
            <div className="hidden lg:flex flex-col justify-between bg-[#0a0a0a] text-white p-12 border-r hairline">
                <div className="flex items-center gap-3">
                    <div className="bg-white p-1.5">
                        <img
                            src={BRAND.logo}
                            alt={BRAND.legal}
                            className="h-10 w-10 object-contain mix-blend-multiply"
                        />
                    </div>
                    <div>
                        <div className="font-display text-lg">
                            QUISEO AUTO REPAIR
                        </div>
                        <div className="font-mono-tech text-[10px] text-white/60">
                            LLC · DISPATCH CONSOLE
                        </div>
                    </div>
                </div>
                <div>
                    <h1 className="font-display text-5xl leading-[0.95] tracking-tighter">
                        Dispatch <br />
                        <span className="text-[#60a5fa]">console.</span>
                    </h1>
                    <p className="text-white/70 mt-6 max-w-md leading-relaxed">
                        Manage incoming bookings, update repair status, and
                        keep your service queue in one place. Mobile-first,
                        keyboard-friendly, no clutter.
                    </p>
                </div>
                <Link
                    to="/"
                    className="font-mono-tech text-[11px] inline-flex items-center gap-2 text-white/60 hover:text-white"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to website
                </Link>
            </div>

            <div className="flex items-center justify-center p-8">
                <form
                    onSubmit={submit}
                    data-testid="admin-login-form"
                    className="w-full max-w-sm"
                >
                    <div className="font-mono-tech text-[11px] text-[#52525b] mb-3">
                        // ADMIN ACCESS
                    </div>
                    <h2 className="font-display text-3xl tracking-tighter mb-2">
                        Sign in
                    </h2>
                    <p className="text-[#52525b] text-sm mb-8">
                        Use the admin credentials provided to you.
                    </p>

                    <label className="font-mono-tech text-[10px] text-[#52525b] block mb-2">
                        Email
                    </label>
                    <input
                        data-testid="admin-email-input"
                        type="email"
                        className={inputCls}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="admin@quiseo.shop"
                        autoComplete="email"
                        required
                    />

                    <label className="font-mono-tech text-[10px] text-[#52525b] block mb-2 mt-5">
                        Password
                    </label>
                    <input
                        data-testid="admin-password-input"
                        type="password"
                        className={inputCls}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                    />

                    <button
                        type="submit"
                        data-testid="admin-login-submit"
                        disabled={busy}
                        className="btn-primary w-full mt-8 px-6 py-4 font-mono-tech text-[12px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                        {busy ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Signing in…
                            </>
                        ) : (
                            <>
                                Sign in <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>

                    <Link
                        to="/"
                        className="font-mono-tech text-[10px] mt-6 inline-flex items-center gap-2 text-[#52525b] hover:text-[#0a0a0a] lg:hidden"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Back to website
                    </Link>
                </form>
            </div>
        </div>
    );
}
